import {ApolloClient} from 'apollo-client';
import {ApolloQueryResult} from 'apollo-client/core/types';

import {
  FetchRegistryProjectsPartialQuery,
  FetchRegistryProjectsPartialQueryResult,
  FetchRegistryProjectsPartialQueryVariables,
  Maybe,
  RegistryProjectsSearchFragmentFragment,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {toMongo} from '../../../util/filters';
import {AccessOption} from '../../../util/permissions';
import {OrganizationMemberRole} from '../../MembersTable/util';
import {RegistryAccessOptionType} from '../common/permissionUtils';
import {GQLRegistryProjectViewerMembershipType} from '../common/types';
import {
  checkRegistryProject,
  isViewerRegistryMemberFromQuery,
  REGISTRY_PROJECT_PREFIX,
} from '../common/utils';
import {FETCH_REGISTRY_COLLECTIONS} from '../graphql/registryCollections.query';
import {FETCH_REGISTRY_PROJECTS_PARTIAL} from '../graphql/registryProjects.query';

type ViewerRegistryMembershipData = {
  isViewerRegMember: boolean;
  canViewCollectionCount: boolean;
  isAvailableToJoin: boolean;
};

export function getAllRegistriesSearchResult(
  data: FetchRegistryProjectsPartialQueryResult['data']
): Maybe<RegistryProjectsSearchFragmentFragment> {
  return data?.organization?.registries ?? null;
}

export const getViewerRegistryMembershipData = ({
  registryAccess,
  viewerMemberRole,
  registry,
}: {
  registryAccess: RegistryAccessOptionType;
  viewerMemberRole: OrganizationMemberRole | undefined;
  registry: GQLRegistryProjectViewerMembershipType;
}): ViewerRegistryMembershipData => {
  const isViewerRegMember = isViewerRegistryMemberFromQuery(registry);

  const canViewCollectionCount =
    registryAccess !== AccessOption.Restricted || isViewerRegMember;

  const isAvailableToJoin =
    registryAccess === AccessOption.Restricted &&
    viewerMemberRole === OrganizationMemberRole.ADMIN &&
    !isViewerRegMember;

  return {
    isViewerRegMember,
    canViewCollectionCount,
    isAvailableToJoin,
  };
};

// Types for the GraphQL responses
export type CollectionEdge = {
  node: {
    project: {name: string; entity: {name: string}};
    type: {name: string};
    name: string;
  };
};
export type RegistryEdge = {node: {name: string}};

type RegistryCollectionsQueryResult = {
  organization?: {
    orgEntity?: {artifactCollections?: {edges: CollectionEdge[]}};
  };
};

type OptionCommon = {
  label: string;
  registryName?: string;
};

export type CollectionOption = OptionCommon & {
  type: 'Collections';
  value: CollectionEdge;
  artifactTypeName?: string;
};

export type RegistryOption = OptionCommon & {
  type: 'Registries';
  value: RegistryEdge;
};

export type BasicSearchOption = RegistryOption | CollectionOption;

export type BasicSearchOptionGroup = {
  label: string;
  options: BasicSearchOption[];
};

/**
 * Fetches registries (registry projects) for the given organization and search term
 */
export async function fetchRegistries<T>(
  apolloClient: ApolloClient<T>,
  orgName: string,
  searchTerm: string
): Promise<RegistryEdge[]> {
  const context = propagateErrorsContext();
  const pattern = searchTerm
    ? `${REGISTRY_PROJECT_PREFIX}%${searchTerm}`
    : REGISTRY_PROJECT_PREFIX;

  const {data}: ApolloQueryResult<FetchRegistryProjectsPartialQuery> =
    await apolloClient.query({
      query: FETCH_REGISTRY_PROJECTS_PARTIAL,
      variables: {
        orgName,
        limit: 10,
        pattern,
      } as FetchRegistryProjectsPartialQueryVariables,
      context,
    });

  return (getAllRegistriesSearchResult(data)?.edges ?? []).filter(
    edge => edge.node?.name
  ) as RegistryEdge[];
}

/**
 * Fetches collections for the given organization and search term
 */
export async function fetchCollections<T>(
  apolloClient: ApolloClient<T>,
  orgName: string,
  searchTerm: string
): Promise<CollectionEdge[]> {
  const context = propagateErrorsContext();
  const collectionFilter = searchTerm
    ? JSON.stringify(
        toMongo({
          key: {section: 'artifact', name: 'name'},
          op: 'CONTAINS',
          value: searchTerm,
        })
      )
    : undefined;

  const {data}: ApolloQueryResult<RegistryCollectionsQueryResult> =
    await apolloClient.query({
      query: FETCH_REGISTRY_COLLECTIONS,
      variables: {
        organization: orgName,
        perPage: 10,
        collectionFilter,
      },
      context,
    });

  return (
    data?.organization?.orgEntity?.artifactCollections?.edges ?? []
  ).filter(edge => edge.node?.name);
}

/**
 * Parses registries and collections responses into options for the select component
 * Handles the logic for deciding how many registries/collections to display
 * Returns a flat array for AutoComplete with group property for grouping
 */
export function getBasicSearchOptions(
  registries: RegistryEdge[],
  collections: CollectionEdge[]
): BasicSearchOption[] {
  // Decide how many registries and collections to display based on the number of results
  let numRegistries = 4;
  let numCollections = 8;
  if (collections.length < numCollections) {
    numRegistries = Math.min(
      10,
      numRegistries + numCollections - collections.length
    );
  } else if (registries.length < numRegistries) {
    numCollections = Math.min(
      10,
      numCollections + numRegistries - registries.length
    );
  }

  const registryOptions: BasicSearchOption[] = registries
    .slice(0, numRegistries)
    .map(registry => {
      const {registryName} = checkRegistryProject(registry.node.name);
      return {
        label: registryName ?? '',
        value: registry,
        type: 'Registries' as const,
        registryName,
      };
    });

  const collectionOptions: BasicSearchOption[] = collections
    .slice(0, numCollections)
    .map(collection => {
      const projectName = collection.node.project?.name;
      const {registryName} = checkRegistryProject(projectName);
      return {
        label: collection.node.name,
        value: collection,
        type: 'Collections' as const,
        registryName: registryName,
        artifactTypeName: collection.node.type?.name,
      };
    });

  return [...registryOptions, ...collectionOptions];
}
